<template>
  <div class="tw-flex tw-flex-col">
    <base-text-field v-model="query" ref="searchInput" class="tw-w-full tw-mb-4" placeholder="Search for a user" size="sm" tabindex="1">
      <template #prefix>
        <search-icon class="tw-h-5 tw-w-5" />
      </template>
    </base-text-field>
    <base-table :loading="isLoading" :fields="fields" :items="items">
      <template #user="{item: user}">
        <div class="tw-whitespace-nowrap tw-text-gray-800">{{ user.fullName }}</div>
        <div class="tw-whitespace-nowrap tw-text-xs tw-text-gray-500">{{ user.email }}</div>
      </template>
      <template #roles="{item: user}">
        <div v-for="role in user.roles" :key="role">
          <div class="tw-text-gray-800">{{ roleDisplay(role) }}</div>
        </div>
      </template>
      <template #locations="{item: user}">
        <div v-for="location in user.locationFilters" :key="location">
          <div class="tw-whitespace-nowrap tw-text-xs">{{ location }}</div>
        </div>
      </template>
      <template #pin="{item: user}">
        <business-user-pin :has-pin="user.hasPin" :user-id="user.userId" />
      </template>
      <template #actions="{item: user}">
        <div class="tw-flex tw-items-center tw-space-x-2">
          <base-button type="flat" @click="edit(user)">
            <template v-slot:icon>
              <edit-icon /> </template
            >Edit
          </base-button>
          <base-button type="flat" @click="del(user)">
            <template v-slot:icon>
              <trash-icon /> </template
            >Delete
          </base-button>
        </div>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-36" />
          <base-skeleton-loader class="tw-h-2 tw-w-48 tw-mt-2" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-36" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-18" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex tw-justify-between tw-gap-3">
            <base-skeleton-loader class="tw-h-8 tw-w-18" />
            <base-skeleton-loader class="tw-h-8 tw-w-18" />
          </div>
        </td>
      </template>
    </base-table>
    <base-pagination v-model="page" class="tw-mt-2" />
    <BusinessUsersEdit :businessId="businessId" ref="edit" @saved="refresh" />
    <business-users-delete :businessId="businessId" ref="confirmationmodal" @close="refresh" />
  </div>
</template>

<script>
import { SearchIcon, EditIcon, TrashIcon } from "@/assets/svgs";
import BusinessUsersEdit from "@/views/business/business-users/components/BusinessUsersEdit";
import BusinessUsersDelete from "@/views/business/business-users/components/BusinessUsersDelete";
import BusinessUserPin from "@/views/business/business-users/components/BusinessUserPin";
import { debounce } from "debounce";

const TAKE_KEY = "predictile_business_users_take";

export default {
  components: {
    SearchIcon,
    BusinessUsersEdit,
    BusinessUsersDelete,
    BusinessUserPin,
    EditIcon,
    TrashIcon,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      items: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      fields: [
        { title: "User", key: "user", thClass: "" },
        { title: "Role", key: "roles", thClass: "" },
        { title: "Locations", key: "locations", thClass: "" },
        { title: "Pin", key: "pin", thClass: "tw-flex-nowrap" },
        { title: "Actions", key: "actions", thClass: "tw-w-0 tw-text-center" },
      ],
      query: null,
      isLoading: false,
    };
  },
  created() {
    this.loadPage();
    this.debouncedLoadPage = debounce(this.loadPage, 200);
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    query() {
      this.page.skip = 0;
      this.debouncedLoadPage();
    },
  },
  methods: {
    edit(item) {
      this.$refs.edit.show(item.userId);
    },
    del(item) {
      this.$refs.confirmationmodal.show(item.userId);
    },
    refresh() {
      this.loadPage();
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    roleDisplay(role) {
      switch (role) {
        case "business_administrator":
          return "Administrator";
        case "business_management":
          return "Management";
        case "business_operations":
          return "Operations";
      }
    },
    loadPage() {
      this.isLoading = true;

      let url = `/businesses/${this.businessId}/users`;
      url += `?skip=${this.page.skip}&take=${this.page.take}`;
      if (this.query) url += `&query=${encodeURIComponent(this.query)}`;

      this.$axios
        .get(url)
        .then((res) => {
          this.items = res.data.items;
          this.page.take = res.data.take;
          this.page.skip = res.data.skip;
          this.page.total = res.data.total;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load users",
            message: "We had a problem retrieving the list of users. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
